import * as Sentry from '@sentry/react';
import { io } from 'socket.io-client';

import type { RealtimeTransport } from './transport';

/**
 * The one real transport, and the only module that imports socket.io-client.
 *
 * The handshake is backend-confirmed (ADO 112249, 2026-08-05): the gateway reads
 * `client.handshake.auth.token` and expects it **`Bearer`-prefixed**, so the scheme is added here
 * rather than by the caller — the provider deals in the bare access token from `tokenStore`, and the
 * wire format is this module's concern. The namespace is whatever `VITE_REALTIME_URL` points at
 * (`ws://<API_HOST>:8080/notifications`). Transports are left at socket.io's default (polling,
 * upgrading to websocket) so a proxy that blocks websockets degrades rather than never connecting.
 */
export const socketIoTransport: RealtimeTransport = ({ url, token, onEvent }) => {
  const socket = io(url, { auth: { token: `Bearer ${token}` } });
  socket.onAny((name: string, payload: unknown) => onEvent(name, payload));

  // ADR-0025's named risk is that this channel "fails quietly (a dropped connection) rather than
  // loudly (a 401)" — a rejected handshake would otherwise be invisible. `once`, not `on`: socket.io
  // retries indefinitely and every attempt fires this, so only the first is worth reporting.
  socket.once('connect_error', (error: Error) => {
    Sentry.captureException(error, { tags: { realtime_connect_error: url } });
  });

  return {
    close: () => {
      socket.close();
    },
  };
};
